import type { ESignField } from '../../api/types/esign.interfaces'
import {
	INLINE_FIELD_FONT_SIZE_PX,
	INLINE_FIELD_HORIZONTAL_PADDING_PX,
	INLINE_FIELD_LINE_HEIGHT_PX,
	INLINE_FIELD_VERTICAL_PADDING_PX,
	PRESET_META,
	PREVIEW_FALLBACK_PAGE_HEIGHT_PX,
	PREVIEW_PAGE_WIDTH_PX,
	TOOL_COLORS,
} from './constants'
import { formatESignDate, getTodayDateValue } from './formatters'
import {
	presetFromFieldMeta,
	presetLabel,
	resolveESignFieldType,
} from './fieldType'

export function isFilledValue(value?: string | null) {
	return typeof value === 'string' && value.trim().length > 0
}

export function getFieldKey(field: ESignField) {
	return field.id ?? `${field.page}-${field.recipientId}-${field.type}`
}

export function getFieldLabel(field: ESignField) {
	if (field.label?.trim()) return field.label.trim()

	const preset = presetFromFieldMeta(
		(field.meta ?? undefined) as Record<string, unknown> | undefined,
	)

	if (preset) return presetLabel(preset)

	return String(field.type ?? 'Field')
}

export function isSignatureLikeField(field: ESignField) {
	const type = resolveESignFieldType(field)
	return type === 'SIGNATURE' || type === 'INITIALS'
}

export function isFieldRequiredForSign(field: ESignField) {
	if (isSignatureLikeField(field)) return true
	return Boolean(field.required)
}

export function isInitialsField(field: ESignField) {
	const upperType = String(field.type ?? '').toUpperCase()
	if (upperType === 'INITIALS') return true
	return resolveESignFieldType(field) === 'INITIALS'
}

export function getFieldPlaceholder(field: ESignField) {
	if (field.placeholder?.trim()) return field.placeholder

	const preset = presetFromFieldMeta(
		(field.meta ?? undefined) as Record<string, unknown> | undefined,
	)

	if (preset) {
		return PRESET_META[preset]?.placeholder ?? 'Enter value'
	}

	if (field.type === 'DATE') return 'D MMM, YYYY'
	if (isSignatureLikeField(field)) return 'Tap to sign'

	return 'Enter value'
}

export function isFieldMultiline(field: ESignField) {
	const upperType = String(field.type ?? 'TEXT').toUpperCase()

	return (
		upperType === 'ADDRESS' ||
		upperType === 'CUSTOM' ||
		upperType === 'TEXT' ||
		upperType === 'NAME' ||
		upperType === 'COMPANY' ||
		upperType === 'TITLE'
	)
}

export function getFieldVisualLineCapacity(
	field: ESignField,
	pageHeightPx = PREVIEW_FALLBACK_PAGE_HEIGHT_PX,
) {
	const availableHeightPx = Math.max(
		INLINE_FIELD_LINE_HEIGHT_PX,
		(field.height / 100) * pageHeightPx - INLINE_FIELD_VERTICAL_PADDING_PX,
	)
	const safeHeightPx = Math.max(
		INLINE_FIELD_LINE_HEIGHT_PX,
		availableHeightPx - 4,
	)
	return Math.max(1, Math.floor(safeHeightPx / INLINE_FIELD_LINE_HEIGHT_PX))
}

export function isFieldVisuallyMultiline(
	field: ESignField,
	pageHeightPx = PREVIEW_FALLBACK_PAGE_HEIGHT_PX,
) {
	return (
		isFieldMultiline(field) && getFieldVisualLineCapacity(field, pageHeightPx) > 1
	)
}

export function isPhoneField(field: ESignField) {
	const upperType = String(field.type ?? 'TEXT').toUpperCase()
	return upperType === 'PHONE'
}

export function sanitizePhoneInput(value: string) {
	return value.replace(/[^0-9+()]/g, '')
}

export function getInlineInputType(field: ESignField) {
	const upperType = String(field.type ?? 'TEXT').toUpperCase()

	if (upperType === 'DATE') return 'date'
	if (upperType === 'EMAIL') return 'email'
	if (upperType === 'PHONE') return 'tel'

	return 'text'
}

export function getFieldVisualCharacterLimit(
	field: ESignField,
	pageHeightPx = PREVIEW_FALLBACK_PAGE_HEIGHT_PX,
) {
	if (isSignatureLikeField(field)) {
		return undefined
	}

	const pageWidthPx = PREVIEW_PAGE_WIDTH_PX
	const availableWidthPx = Math.max(
		40,
		(field.width / 100) * pageWidthPx - INLINE_FIELD_HORIZONTAL_PADDING_PX,
	)
	const safeWidthPx = Math.max(20, availableWidthPx - 10)

	const avgCharWidth = INLINE_FIELD_FONT_SIZE_PX * 0.54
	const charsPerLine = Math.max(1, Math.floor(safeWidthPx / avgCharWidth))
	const upperType = resolveESignFieldType(field)

	if (!isFieldMultiline(field)) {
		return charsPerLine + 2 + (upperType === 'NAME' ? 4 : 0)
	}

	const lineCapacity = isFieldVisuallyMultiline(field, pageHeightPx)
		? getFieldVisualLineCapacity(field, pageHeightPx)
		: 1

	if (lineCapacity <= 1) {
		return charsPerLine + 2 + (upperType === 'NAME' ? 4 : 0)
	}

	if (lineCapacity === 2) {
		return Math.max(charsPerLine + 1, charsPerLine * 2 - 2)
	}

	return charsPerLine * (lineCapacity - 1)
}

export function clampFieldValue(
	field: ESignField,
	value: string,
	pageHeightPx = PREVIEW_FALLBACK_PAGE_HEIGHT_PX,
) {
	if (isSignatureLikeField(field)) {
		return value
	}

	const characterLimit = getFieldVisualCharacterLimit(field, pageHeightPx)
	if (!characterLimit) return value
	return value.slice(0, characterLimit)
}

export function getFieldColor(field: ESignField) {
	const upperType = resolveESignFieldType(field)

	if (upperType === 'SIGNATURE') return TOOL_COLORS.SIGNATURE
	if (upperType === 'INITIALS') return TOOL_COLORS.INITIALS

	const preset = presetFromFieldMeta(
		(field.meta ?? undefined) as Record<string, unknown> | undefined,
	)

	return (
		TOOL_COLORS[(preset ?? 'CUSTOM') as keyof typeof TOOL_COLORS] ??
		TOOL_COLORS.CUSTOM
	)
}

export function isDateField(field: ESignField) {
	return resolveESignFieldType(field) === 'DATE'
}

export function getInitialFieldValue(
	field: ESignField,
	recipientName?: string,
	recipientEmail?: string,
) {
	if (typeof field.value === 'string' && field.value.trim()) return field.value

	switch (resolveESignFieldType(field)) {
		case 'NAME':
			return recipientName ?? ''
		case 'EMAIL':
			return recipientEmail ?? ''
		case 'DATE':
			return getTodayDateValue()
		default:
			return ''
	}
}

export { formatESignDate, getTodayDateValue }
